import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getIncidents } from '@/api/incidents';
import { useAuth } from '@/contexts/AuthContext';
import type { Incident } from '@/types/community/incident';

interface IncidentSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onIncidentDataChange?: (incident: { id: string; name: string } | null) => void;
}

export const IncidentSelector: React.FC<IncidentSelectorProps> = ({
  disabled = false,
  fieldName = 'incidentId',
  onIncidentDataChange
}) => {
  const { t } = useTranslation(['incidentReporting', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch incidents
  const { data: incidentsData, error } = useQuery({
    queryKey: ['incidents', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getIncidents({ 
        firestoreUser,
        pageSize: 100 // Get all incidents for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize incident data from form values when component mounts or form values change
  useEffect(() => {
    const incidentId = form.getFieldValue(fieldName);
    if (incidentId && incidentsData?.incidents) {
      const incident = incidentsData.incidents.find((i: Incident) => i.id === incidentId);
      onIncidentDataChange?.(incident ? { 
        id: incident.id, 
        name: incident.title 
      } : null);
    }
  }, [form, incidentsData, onIncidentDataChange, fieldName]);

  const handleIncidentChange = (incidentId: string) => {
    // Provide incident data to parent
    if (incidentId) {
      const incident = incidentsData?.incidents?.find((i: Incident) => i.id === incidentId);
      onIncidentDataChange?.(incident ? { 
        id: incident.id, 
        name: incident.title 
      } : null);
    } else {
      onIncidentDataChange?.(null);
    }
  };

  const incidentOptions = incidentsData?.incidents?.map((incident: Incident) => ({
    value: incident.id,
    label: incident.title,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('incidentReporting:fields.title')}
      >
        <Select
          placeholder={t('incidentReporting:fields.selectIncident')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('incidentReporting:fields.title')}
    >
      <Select
        placeholder={t('incidentReporting:fields.selectIncident')}
        onChange={handleIncidentChange}
        disabled={disabled}
        allowClear
        options={incidentOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 